import React, { lazy, Suspense } from "react";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { useUser } from "@/shared/lib/hooks/useUser/useUser";
import { useAdminUI } from "@/shared/config/admin";
import { memberPath } from "@/features/clubMarathons/model/marathonFlavor";

/*
 * /:lang/account/league-users/* — «Лига участников», пока только для
 * администраторов.
 *
 * До страницы этот адрес ловил общий маршрут «*» (routeConfig, NOT_FOUND) и
 * отвечал голым «404» всем: и не вошедшим, и вошедшим. Ребёнок кабинета
 * (как было в первой версии) это менял: не вошедшего RequireAuth уводил на
 * вход, вошедший видел «404» внутри каркаса кабинета. Поэтому маршрут плоский
 * и сам решает:
 *   не вошёл                                     → «404», и ни одного запроса;
 *   вошёл, не администратор или «как обычный»    → «404»;
 *     … но marathons/<id> — на страницу клуба к блокам этого марафона: туда ведут уведомления
 *     марафонов клуба (site_server marathon_notify), а участник видит марафон своего клуба
 *     там (решение 03.11 (a));
 *   администратор                                → каркас кабинета + лига.
 * Пока грузится список администраторов — пусто (решать не из чего).
 */

const AccountPage = lazy(() => import("@/pages/Account").then((m) => ({ default: m.default })));
const LeagueUsersPage = lazy(() => import("@/pages/LeagueUsersPage").then((m) => ({ default: m.LeagueUsersPage })));

/** Ровно то, что рисует NOT_FOUND в routeConfig. */
const NotFound = () => <div>404</div>;

/** id марафона из хвоста «marathons/<id>» (или null). */
export const marathonOfTail = (tail: string | undefined): string | null => {
    const m = /^marathons\/([^/]+)\/?$/.exec(tail || "");
    if (!m) return null;
    try {
        return decodeURIComponent(m[1]);
    } catch {
        return m[1];
    }
};

export const LeagueUsersRoute = () => {
    const { user } = useUser();
    const email = user && typeof user !== "string" ? user.email || "" : "";
    if (!email) return <NotFound />;
    return <AdminOnly email={email} />;
};

const AdminOnly = ({ email }: { email: string }) => {
    const { isAdmin, loading } = useAdminUI(email);
    const params = useParams<{ lang: string; "*": string }>();
    if (loading) return <div data-testid="league-users-checking" />;
    if (!isAdmin) {
        const id = marathonOfTail(params["*"]);
        return id ? <Navigate to={memberPath(id, params.lang || "ru", null)} replace /> : <NotFound />;
    }
    // Хвост адреса (me, person/:id, …) — дальше, в страницу лиги: она читает
    // его из useParams()["*"], как и раньше, когда была ребёнком кабинета.
    return (
        <Routes>
            <Route
                element={
                    <Suspense fallback="">
                        <AccountPage />
                    </Suspense>
                }
            >
                <Route
                    path="*"
                    element={
                        <Suspense fallback="">
                            <LeagueUsersPage />
                        </Suspense>
                    }
                />
            </Route>
        </Routes>
    );
};

export default LeagueUsersRoute;
